import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import type {
  FormManagementQuestionChart,
  FormManagementQuestionChartKind,
  FormManagementQuestionChartSegment,
} from '@/contracts/form-management'
import { cn } from '@/lib/utils'
import {
  AlignLeft,
  BarChart3,
  CalendarDays,
  FileText,
  Grid3X3,
  ListOrdered,
  PieChart,
  Sigma,
} from 'lucide-react'

const chartKindLabels: Record<FormManagementQuestionChartKind, string> = {
  donut: '圓環分佈',
  horizontal_bar: '長條分佈',
  stacked_bar: '多選堆疊',
  histogram: '分數分佈',
  numeric_summary: '數字摘要',
  timeline: '時間分佈',
  text_cloud: '關鍵詞雲',
  sample_list: '格式樣本',
  ranking: '排序平均名次',
  matrix: '矩陣列統計',
  file_summary: '檔案摘要',
  layout_block: '版面區塊',
}

const chartKindIcons = {
  donut: PieChart,
  horizontal_bar: BarChart3,
  stacked_bar: BarChart3,
  histogram: BarChart3,
  numeric_summary: Sigma,
  timeline: CalendarDays,
  text_cloud: AlignLeft,
  sample_list: FileText,
  ranking: ListOrdered,
  matrix: Grid3X3,
  file_summary: FileText,
  layout_block: AlignLeft,
} satisfies Record<FormManagementQuestionChartKind, typeof PieChart>

const chartKindOrder = [
  'donut',
  'horizontal_bar',
  'stacked_bar',
  'histogram',
  'numeric_summary',
  'timeline',
  'text_cloud',
  'sample_list',
  'ranking',
  'matrix',
  'file_summary',
  'layout_block',
] as const satisfies readonly FormManagementQuestionChartKind[]

type FormManagementQuestionChartsProps = {
  charts: FormManagementQuestionChart[]
}

export function FormManagementQuestionCharts({ charts }: FormManagementQuestionChartsProps) {
  const chartKindSummary = chartKindOrder
    .map((kind) => ({
      kind,
      count: charts.filter((chart) => chart.chartKind === kind).length,
    }))
    .filter((item) => item.count > 0)

  return (
    <section className="space-y-4" aria-labelledby="form-management-question-charts-title">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h3 id="form-management-question-charts-title" className="font-semibold text-slate-950">
            逐題圖表統計
          </h3>
          <p className="mt-1 text-sm leading-6 text-slate-500">
            每個題型使用適合的 mock-safe 統計呈現，資料只做前端示意，不代表真實回應。
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge>Mock-safe</Badge>
          <Badge variant="outline">{charts.length} 個題型</Badge>
        </div>
      </div>

      {chartKindSummary.length > 0 ? (
        <div className="rounded-md border border-slate-200 bg-white p-3">
          <div className="text-xs font-semibold text-slate-600">圖表類型總覽</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {chartKindSummary.map((item) => {
              const Icon = chartKindIcons[item.kind]

              return (
                <span
                  key={item.kind}
                  className="inline-flex items-center gap-1.5 rounded-md bg-slate-50 px-2 py-1 text-xs font-medium text-slate-700"
                >
                  <Icon className="size-3.5 text-indigo-600" />
                  {chartKindLabels[item.kind]} {item.count}
                </span>
              )
            })}
          </div>
        </div>
      ) : null}

      {charts.length > 0 ? (
        <div className="grid gap-4">
          {charts.map((chart, index) => (
            <QuestionChartCard key={chart.id} chart={chart} index={index} />
          ))}
        </div>
      ) : (
        <output className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center">
          <div className="font-medium text-slate-800">目前沒有可呈現的題目統計</div>
          <p className="mt-2 text-sm leading-6 text-slate-500">
            沒有 mock 題目回答資料時，不顯示逐題圖表，避免把示意資料誤讀為真實回應。
          </p>
        </output>
      )}
    </section>
  )
}

function QuestionChartCard({
  chart,
  index,
}: { chart: FormManagementQuestionChart; index: number }) {
  const Icon = chartKindIcons[chart.chartKind]
  const isLayoutBlock = chart.chartKind === 'layout_block'

  return (
    <Card data-testid={`question-chart-${chart.questionType}`} className="overflow-hidden">
      <CardContent className="p-0">
        <div className="border-b border-slate-200 bg-white p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600">
                  Q{index + 1}
                </span>
                <Badge variant="secondary">{chart.typeLabel}</Badge>
                <Badge variant="outline" className="gap-1">
                  <Icon className="size-3.5" />
                  {chartKindLabels[chart.chartKind]}
                </Badge>
                {chart.suppression?.suppressed ? <Badge variant="warning">小樣本</Badge> : null}
              </div>
              <h4 className="mt-3 text-base font-semibold leading-6 text-slate-950">
                {chart.questionTitle}
              </h4>
              <p className="mt-1 text-sm leading-6 text-slate-500">{chart.summary}</p>
              {chart.suppression?.suppressed ? (
                <div
                  role="note"
                  className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                >
                  樣本數不足，僅供參考。
                </div>
              ) : null}
            </div>
            <div className="grid grid-cols-3 gap-2 text-center sm:min-w-80">
              <MetricPill label="回答" value={chart.totalAnswers} />
              <MetricPill
                label="完成率"
                value={isLayoutBlock ? '不適用' : `${chart.completionRate}%`}
              />
              <MetricPill label="略過" value={isLayoutBlock ? '不列入統計' : chart.skipped} />
            </div>
          </div>
        </div>
        <div className="p-4">
          <QuestionChartVisual chart={chart} />
        </div>
      </CardContent>
    </Card>
  )
}

function MetricPill({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 font-semibold text-slate-950">{value}</div>
    </div>
  )
}

function QuestionChartVisual({ chart }: { chart: FormManagementQuestionChart }) {
  if (chart.chartKind === 'donut') return <DonutChart chart={chart} />
  if (chart.chartKind === 'stacked_bar') return <StackedChart chart={chart} />
  if (chart.chartKind === 'histogram') return <HistogramChart chart={chart} />
  if (chart.chartKind === 'numeric_summary') return <NumericSummaryChart chart={chart} />
  if (chart.chartKind === 'text_cloud') return <TextCloudChart chart={chart} />
  if (chart.chartKind === 'sample_list') return <SampleListChart chart={chart} />
  if (chart.chartKind === 'timeline') return <TimelineChart chart={chart} />
  if (chart.chartKind === 'ranking') return <RankingChart chart={chart} />
  if (chart.chartKind === 'matrix') return <MatrixChart chart={chart} />
  if (chart.chartKind === 'file_summary') return <FileSummaryChart chart={chart} />
  if (chart.chartKind === 'layout_block') return <LayoutBlockChart chart={chart} />

  return <BarRows segments={chart.segments} testId="chart-kind-horizontal-bar" />
}

function DonutChart({ chart }: { chart: FormManagementQuestionChart }) {
  return (
    <div
      data-testid="chart-kind-donut"
      className="grid gap-5 lg:grid-cols-[220px_minmax(0,1fr)] lg:items-center"
    >
      <div className="grid place-items-center">
        <div
          role="img"
          aria-label={`${chart.questionTitle} 圓環分佈`}
          className="relative size-40 rounded-full"
          style={{ background: buildConicGradient(chart.segments) }}
        >
          <div className="absolute inset-8 grid place-items-center rounded-full bg-white text-center shadow-inner">
            <div>
              <div className="text-2xl font-semibold text-slate-950">{chart.totalAnswers}</div>
              <div className="text-xs text-slate-500">回答</div>
            </div>
          </div>
        </div>
      </div>
      <LegendRows segments={chart.segments} />
    </div>
  )
}

function StackedChart({ chart }: { chart: FormManagementQuestionChart }) {
  return (
    <div data-testid="chart-kind-stacked-bar" className="space-y-4">
      <BarRows segments={chart.segments} />
      {chart.stacks?.map((stack) => (
        <div key={stack.id} className="rounded-md border border-slate-200 bg-slate-50 p-3">
          <div className="mb-2 flex items-center justify-between gap-2 text-sm">
            <span className="font-medium text-slate-800">{stack.label}</span>
            <span className="text-slate-500">{stack.total} 份</span>
          </div>
          <StackedBar segments={stack.segments} />
        </div>
      ))}
    </div>
  )
}

function HistogramChart({ chart }: { chart: FormManagementQuestionChart }) {
  return (
    <div data-testid="chart-kind-histogram" className="space-y-4">
      {chart.numericSummary ? (
        <div className="grid gap-2 sm:grid-cols-4">
          <MetricPill
            label="平均"
            value={`${chart.numericSummary.average}${chart.numericSummary.unit ?? ''}`}
          />
          <MetricPill
            label="中位數"
            value={`${chart.numericSummary.median}${chart.numericSummary.unit ?? ''}`}
          />
          <MetricPill label="最小" value={chart.numericSummary.min} />
          <MetricPill label="最大" value={chart.numericSummary.max} />
        </div>
      ) : null}
      <div className="flex h-52 items-end gap-2 rounded-md bg-slate-50 p-4">
        {chart.segments.map((segment) => (
          <div key={segment.id} className="flex min-w-12 flex-1 flex-col items-center gap-2">
            <div className="flex h-36 w-full items-end justify-center">
              <div
                className="w-full rounded-t-md"
                style={{
                  backgroundColor: segment.color,
                  height: `${Math.max(8, segment.percent)}%`,
                }}
              />
            </div>
            <div className="text-center text-xs text-slate-600">{segment.label}</div>
            <div className="text-xs font-medium text-slate-950">{segment.value}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

function NumericSummaryChart({ chart }: { chart: FormManagementQuestionChart }) {
  const summary = chart.numericSummary
  return (
    <div data-testid="chart-kind-numeric-summary" className="space-y-4">
      {summary ? (
        <div className="grid gap-2 sm:grid-cols-4">
          <MetricPill label="最小" value={`${summary.min}${summary.unit ?? ''}`} />
          <MetricPill label="最大" value={`${summary.max}${summary.unit ?? ''}`} />
          <MetricPill label="平均" value={`${summary.average}${summary.unit ?? ''}`} />
          <MetricPill label="中位數" value={`${summary.median}${summary.unit ?? ''}`} />
        </div>
      ) : null}
      <BarRows segments={chart.segments} />
    </div>
  )
}

function TextCloudChart({ chart }: { chart: FormManagementQuestionChart }) {
  return (
    <div data-testid="chart-kind-text-cloud" className="grid gap-4 lg:grid-cols-[1fr_320px]">
      <div className="flex min-h-44 flex-wrap items-center justify-center gap-3 rounded-md bg-slate-50 p-5">
        {chart.tokens?.map((token) => (
          <span
            key={token.id}
            className="rounded-md bg-white px-2 py-1 font-semibold shadow-sm"
            style={{
              color: token.weight > 4 ? '#0F766E' : token.weight > 2 ? '#2563EB' : '#64748B',
              fontSize: `${12 + token.weight * 3}px`,
            }}
          >
            {token.label}
          </span>
        ))}
      </div>
      <SampleRows samples={chart.samples ?? []} />
    </div>
  )
}

function SampleListChart({ chart }: { chart: FormManagementQuestionChart }) {
  return (
    <div data-testid="chart-kind-sample-list" className="grid gap-4 lg:grid-cols-[1fr_320px]">
      <BarRows segments={chart.segments} />
      <SampleRows samples={chart.samples ?? []} />
    </div>
  )
}

function TimelineChart({ chart }: { chart: FormManagementQuestionChart }) {
  return (
    <div data-testid="chart-kind-timeline">
      <BarRows segments={chart.segments} />
    </div>
  )
}

function RankingChart({ chart }: { chart: FormManagementQuestionChart }) {
  return (
    <div data-testid="chart-kind-ranking" className="space-y-3">
      {chart.segments.map((segment, index) => (
        <div key={segment.id} className="grid gap-2 md:grid-cols-[140px_minmax(0,1fr)_110px]">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
            <span className="grid size-6 place-items-center rounded bg-emerald-50 text-xs text-emerald-700">
              {index + 1}
            </span>
            {segment.label}
          </div>
          <div className="min-w-0 rounded-full bg-slate-100">
            <div
              className="h-3 rounded-full bg-emerald-500"
              style={{ width: `${Math.max(8, segment.percent)}%` }}
            />
          </div>
          <div className="text-sm text-slate-500">{segment.helper}</div>
        </div>
      ))}
    </div>
  )
}

function MatrixChart({ chart }: { chart: FormManagementQuestionChart }) {
  return (
    <div data-testid="chart-kind-matrix" className="space-y-3">
      <p className="text-xs text-slate-500">手機或窄版畫面可左右滑動矩陣列統計。</p>
      <section
        aria-label={`${chart.questionTitle} 矩陣列統計橫向捲動區`}
        className="overflow-x-auto rounded-md border border-slate-200 bg-white"
      >
        <div className="min-w-[640px] space-y-3 p-3">
          {chart.stacks?.map((stack) => (
            <div key={stack.id} className="grid gap-2 md:grid-cols-[160px_minmax(0,1fr)]">
              <div className="text-sm font-medium text-slate-800">{stack.label}</div>
              <StackedBar segments={stack.segments} />
            </div>
          ))}
          <LegendRows segments={chart.stacks?.[0]?.segments ?? []} compact />
        </div>
      </section>
    </div>
  )
}

function FileSummaryChart({ chart }: { chart: FormManagementQuestionChart }) {
  const fileSummary = chart.fileSummary
  return (
    <div data-testid="chart-kind-file-summary" className="space-y-4">
      {fileSummary ? (
        <div className="grid gap-2 sm:grid-cols-3">
          <MetricPill label="檔案數" value={fileSummary.totalFiles} />
          <MetricPill label="平均上傳" value={`${fileSummary.averageFilesPerAnswer} 件`} />
          <MetricPill label="總容量" value={`${fileSummary.totalSizeMb.toFixed(1)} MB`} />
        </div>
      ) : null}
      <div className="grid gap-4 lg:grid-cols-2">
        <BarRows segments={fileSummary?.fileTypes ?? chart.segments} />
        <BarRows segments={fileSummary?.statuses ?? []} />
      </div>
    </div>
  )
}

function LayoutBlockChart({ chart }: { chart: FormManagementQuestionChart }) {
  return (
    <div
      data-testid="chart-kind-layout-block"
      className="grid gap-3 rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 md:grid-cols-[1fr_280px]"
    >
      <div>
        <div className="font-medium text-slate-900">版面區塊</div>
        <p className="mt-1 text-sm leading-6 text-slate-500">
          此題型用來呈現說明或分段，不收集回答，因此不會產生選項、文字或數值圖表。
        </p>
      </div>
      <SampleRows samples={chart.samples ?? []} />
    </div>
  )
}

function BarRows({
  segments,
  testId,
}: {
  segments: FormManagementQuestionChartSegment[]
  testId?: string
}) {
  const maxValue = Math.max(...segments.map((segment) => segment.value), 1)

  return (
    <div data-testid={testId} className="space-y-3">
      {segments.map((segment) => (
        <div key={segment.id} className="grid gap-2 md:grid-cols-[160px_minmax(0,1fr)_90px]">
          <div className="truncate text-sm font-medium text-slate-800">{segment.label}</div>
          <div className="min-w-0 rounded-full bg-slate-100">
            <div
              className="h-3 rounded-full"
              style={{
                backgroundColor: segment.color,
                width: `${Math.max(6, (segment.value / maxValue) * 100)}%`,
              }}
            />
          </div>
          <div className="text-sm text-slate-500">
            {segment.value} / {segment.percent}%
          </div>
        </div>
      ))}
    </div>
  )
}

function StackedBar({ segments }: { segments: FormManagementQuestionChartSegment[] }) {
  return (
    <div className="flex h-4 overflow-hidden rounded-full bg-slate-100">
      {segments.map((segment) => (
        <div
          key={segment.id}
          className="min-w-1"
          style={{
            backgroundColor: segment.color,
            width: `${Math.max(4, segment.percent)}%`,
          }}
          aria-label={`${segment.label} ${segment.percent}%`}
        />
      ))}
    </div>
  )
}

function LegendRows({
  segments,
  compact = false,
}: {
  segments: FormManagementQuestionChartSegment[]
  compact?: boolean
}) {
  return (
    <div className={cn('grid gap-2', compact && 'sm:grid-cols-3')}>
      {segments.map((segment) => (
        <div
          key={segment.id}
          className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
        >
          <div className="flex min-w-0 items-center gap-2">
            <span
              className="size-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: segment.color }}
            />
            <span className="truncate font-medium text-slate-800">{segment.label}</span>
          </div>
          <span className="shrink-0 text-slate-500">
            {segment.value} / {segment.percent}%
          </span>
        </div>
      ))}
    </div>
  )
}

function SampleRows({
  samples,
}: {
  samples: NonNullable<FormManagementQuestionChart['samples']>
}) {
  return (
    <div className="space-y-2">
      {samples.map((sample) => (
        <div key={sample.id} className="rounded-md border border-slate-200 bg-white px-3 py-2">
          <div className="text-xs text-slate-500">{sample.label}</div>
          <div className="mt-1 break-words text-sm font-medium text-slate-900">{sample.value}</div>
          {sample.helper ? (
            <div className="mt-1 text-xs text-slate-500">{sample.helper}</div>
          ) : null}
        </div>
      ))}
    </div>
  )
}

function buildConicGradient(segments: FormManagementQuestionChartSegment[]) {
  if (segments.length === 0) return '#E2E8F0'
  let cursor = 0
  const stops = segments.map((segment) => {
    const start = cursor
    const end = Math.min(100, cursor + segment.percent)
    cursor = end
    return `${segment.color ?? '#94A3B8'} ${start}% ${end}%`
  })

  return `conic-gradient(${stops.join(', ')})`
}
